import { useCallback, useEffect, useState } from "react";
import { usePromise } from "react-use";
import { useAppContext } from "./useAppContext";
import { useBusyBlockerContext } from "../contexts/busy-blocker-context";
import { useErrorHandling } from "./useErrorHandling";
import { useInteractiveMessage } from "./useInteractiveMessage";
import { useMessage } from "./useMessage";
import { useSelectedZone } from "./useSelection";
import { useTextZoneDashboard } from "./useTextZoneDashboard";

interface TextZoneFontSizeState {
	currentSize: number;
	minSize: number;
	maxSize: number;
	canDecreaseSize: boolean;
	canIncreaseSize: boolean;
	disabled: boolean;
	onChange: (requestedValue: number) => void;
}

export function useTextZoneFontSize(): TextZoneFontSizeState {
	const selectedZone = useSelectedZone();
	const dashboard = useTextZoneDashboard();
	const { editor } = useAppContext();
	const { busyBlocker } = useBusyBlockerContext();
	const { createErrorHandler } = useErrorHandling();
	const resolveIfMounted = usePromise();
	const { markInteractiveMessage, makeNonInteractive, makeNonInteractiveDebounced } =
		useInteractiveMessage(editor.commandManager);

	const textZone = eXpress.core.isAnyTextZone(selectedZone) ? selectedZone : undefined;

	const [currentSize, setCurrentSize] = useState(20);
	const [minSize, setMinSize] = useState(20);
	const [maxSize, setMaxSize] = useState(20);
	const [canDecreaseSize, setCanDecreaseSize] = useState(false);
	const [canIncreaseSize, setCanIncreaseSize] = useState(false);

	const updateState = useCallback(
		(forTextZone: eXpress.core.AnyTextZone | undefined) => {
			const forFontSize = forTextZone ? forTextZone.textSettings.ptFontSize : 20;
			setCurrentSize(forFontSize);
			setMinSize(forTextZone ? forTextZone.minimumFontSizeInPt || forFontSize : forFontSize);
			setMaxSize(forTextZone ? forTextZone.maximumFontSizeInPt || forFontSize : forFontSize);
			setCanDecreaseSize(
				!!forTextZone && dashboard.canDecreaseFontSize(forTextZone, forFontSize - 1),
			);
			setCanIncreaseSize(
				!!forTextZone && dashboard.canIncreaseFontSize(forTextZone, forFontSize + 1),
			);

			if (
				forTextZone &&
				(!forTextZone.minimumFontSizeInPt || !forTextZone.maximumFontSizeInPt)
			) {
				resolveIfMounted(
					busyBlocker.during(
						dashboard.getAllowedFontSizeRangeAsync(forTextZone).then((range) => {
							forTextZone.minimumFontSizeInPt = range.minimumFontSizeInPt;
							forTextZone.maximumFontSizeInPt = range.maximumFontSizeInPt;
						}),
					),
				)
					.then(() => {
						updateState(forTextZone);
					})
					.catch(createErrorHandler());
			}
		},
		[dashboard, resolveIfMounted, busyBlocker, createErrorHandler],
	);

	useEffect(() => {
		updateState(textZone);
	}, [textZone, updateState]);

	useEffect(
		() => () => {
			makeNonInteractive();
		},
		[makeNonInteractive],
	);

	useMessage(
		useCallback(() => {
			const selectedZone2 = editor.selection.getCurrentZone();
			const textZone2 = eXpress.core.isAnyTextZone(selectedZone2) ? selectedZone2 : undefined;
			updateState(textZone2);
		}, [editor.selection, updateState]),
		{
			captureInteractiveMessages: true,
		},
	);

	const onChange = useCallback(
		(requestedValue: number) => {
			if (textZone && dashboard.canSetFontSize(textZone, requestedValue)) {
				dashboard.setFontSize(textZone, requestedValue, true);
				markInteractiveMessage();
				makeNonInteractiveDebounced().catch(createErrorHandler());
			}
		},
		[
			textZone,
			dashboard,
			markInteractiveMessage,
			makeNonInteractiveDebounced,
			createErrorHandler,
		],
	);

	return {
		currentSize,
		minSize,
		maxSize,
		canDecreaseSize,
		canIncreaseSize,
		disabled: !canDecreaseSize && !canIncreaseSize,
		onChange,
	};
}
